import './ListPager.css';

type Props = {
  page: number;
  totalPages: number;
  totalElements: number;
  loading?: boolean;
  onChange: (page: number) => void;
};

/** 현재 페이지 주변 + 양끝만 남기고 나머지는 생략 */
function pageItems(current: number, total: number): Array<number | '…'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const set = new Set<number>([0, total - 1, current]);
  for (let d = 1; d <= 1; d += 1) {
    if (current - d >= 0) set.add(current - d);
    if (current + d < total) set.add(current + d);
  }
  if (current <= 2) {
    set.add(2);
    set.add(3);
  }
  if (current >= total - 3) {
    set.add(total - 3);
    set.add(total - 4);
  }
  const sorted = Array.from(set)
    .filter((n) => n >= 0 && n < total)
    .sort((a, b) => a - b);
  const out: Array<number | '…'> = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('…');
    out.push(sorted[i]);
  }
  return out;
}

function ListPager({ page, totalPages, totalElements, loading, onChange }: Props) {
  if (totalElements <= 0 && totalPages <= 0) return null;

  const safeTotal = Math.max(0, totalPages);
  const safePage = Math.min(Math.max(0, page), Math.max(0, safeTotal - 1));
  const items = safeTotal > 0 ? pageItems(safePage, safeTotal) : [];
  const showPages = safeTotal > 0;

  return (
    <nav className="list-pager" aria-label="목록 페이지">
      <div className="list-pager__meta">
        <span className="list-pager__count">
          총 <strong>{totalElements.toLocaleString()}</strong>건
        </span>
        {showPages ? (
          <span className="list-pager__page-label">
            {safePage + 1} / {safeTotal}
          </span>
        ) : null}
      </div>

      {showPages ? (
        <div className="list-pager__actions">
          <button
            type="button"
            className="list-pager__nav"
            disabled={safePage <= 0 || loading}
            onClick={() => onChange(Math.max(0, safePage - 1))}
            aria-label="이전 페이지"
          >
            <span aria-hidden>‹</span>
            <span className="list-pager__nav-text">이전</span>
          </button>

          <div className="list-pager__pages" role="list">
            {items.map((item, idx) =>
              item === '…' ? (
                <span key={`e-${idx}`} className="list-pager__ellipsis" aria-hidden>
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  role="listitem"
                  className={`list-pager__page${item === safePage ? ' is-active' : ''}`}
                  disabled={loading}
                  aria-current={item === safePage ? 'page' : undefined}
                  aria-label={`${item + 1} 페이지`}
                  onClick={() => onChange(item)}
                >
                  {item + 1}
                </button>
              )
            )}
          </div>

          <button
            type="button"
            className="list-pager__nav"
            disabled={safeTotal === 0 || safePage >= safeTotal - 1 || loading}
            onClick={() => onChange(safePage + 1)}
            aria-label="다음 페이지"
          >
            <span className="list-pager__nav-text">다음</span>
            <span aria-hidden>›</span>
          </button>
        </div>
      ) : null}
    </nav>
  );
}

export default ListPager;
